'use client';

import { useEffect, useState } from 'react';

import { Logo } from '@/components/brand/logo';
import { buttonVariants } from '@/components/ui/button';
import { collectionUrlFor } from '@/lib/host';
import { cn } from '@/lib/utils';

const SAMPLE_SUBDOMAIN = 'archive';

const STEPS: { n: string; title: string; body: string }[] = [
  {
    n: '01',
    title: 'Bring the archive.',
    body: 'Upload newsletters, research notes, transcripts, URLs, or files that make your paid work worth returning to.',
  },
  {
    n: '02',
    title: 'Choose who can use it.',
    body: 'Keep access private while you test, then gate it to the subscribers who already pay for your work.',
  },
  {
    n: '03',
    title: 'Share one subscriber link.',
    body: 'Subscribers connect once and get source-cited answers from your archive in the AI tools they already use.',
  },
];

const SOURCE_CARDS: { label: string; title: string; meta: string }[] = [
  {
    label: 'Issue 42',
    title: 'The compounding archive',
    meta: 'subscriber-only essay',
  },
  {
    label: 'Research note',
    title: 'Retention after the first month',
    meta: '3 cited passages',
  },
  {
    label: 'Transcript 18',
    title: 'Why readers renew',
    meta: '2 cited passages',
  },
];

type MarketingLandingProps = {
  rootDomain: string;
};

export function MarketingLanding({ rootDomain }: MarketingLandingProps) {
  const sampleConnectionUrl = collectionUrlFor(
    SAMPLE_SUBDOMAIN,
    rootDomain,
    '/mcp'
  );
  const sampleDisplayUrl = sampleConnectionUrl.replace(/^https?:\/\//, '');

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[var(--bg-deep)] text-[var(--fg)] font-sans">
      <MarketingNav />
      <Hero sampleDisplayUrl={sampleDisplayUrl} />
      <HowItWorks />
      <AuthorValue />
      <CitedAnswerProof sampleDisplayUrl={sampleDisplayUrl} />
      <MarketingFooter />
    </div>
  );
}

function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={cn(
        'sticky top-0 z-50 box-border flex w-full items-center gap-7 px-6 md:px-10 py-4 transition-[background,border-color] duration-200',
        scrolled
          ? 'bg-[color-mix(in_oklab,var(--bg-deep)_88%,transparent)] backdrop-blur-md border-b border-[var(--hairline)]'
          : 'bg-transparent border-b border-transparent'
      )}
    >
      <a href="/">
        <Logo wordmarkSize={18} wordmarkClassName="text-[var(--fg)]" />
      </a>
      <div className="hidden md:flex items-center gap-6 ml-5">
        {[
          { label: 'Discover', href: '#discover' },
          { label: 'How it works', href: '#how-it-works' },
          { label: 'Docs', href: 'https://docs.findlore.dev' },
        ].map((l) => (
          <a
            key={l.label}
            href={l.href}
            className="text-[14px] text-[var(--fg-muted)] hover:text-[var(--fg)] transition-colors"
          >
            {l.label}
          </a>
        ))}
      </div>
      <div className="flex-1" />
      <a
        href="/login"
        className={cn(
          buttonVariants({ variant: 'signal' }),
          'h-9 rounded px-4 text-[14px] font-medium normal-case tracking-normal',
          'active:translate-y-px active:shadow-[var(--shadow-press)] active:scale-[0.97]'
        )}
      >
        Sign in
      </a>
    </nav>
  );
}

function Hero({ sampleDisplayUrl }: { sampleDisplayUrl: string }) {
  return (
    <section
      id="discover"
      className="box-border w-full px-6 md:px-10 pt-16 md:pt-20 pb-24 md:pb-32 max-w-[1200px] mx-auto"
      style={{ maxWidth: 'min(1200px, 100vw)' }}
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--accent-text)] mb-6">
        new value for paid authors - now in open beta
      </div>
      <h1 className="font-display text-[44px] sm:text-[68px] md:text-[94px] font-medium leading-[0.98] tracking-[-0.02em] text-[var(--fg)] max-w-[11ch] md:max-w-[13ch] m-0 text-balance">
        An AI-ready archive for your paid subscribers
        <span className="text-[var(--reward)]">.</span>
      </h1>
      <p className="max-w-[min(342px,calc(100vw-3rem))] font-display italic text-[20px] leading-[1.4] text-[color-mix(in_oklab,var(--fg)_64%,var(--fg-muted))] mt-7 sm:max-w-[52ch] md:text-[22px]">
        Lore turns newsletters, transcripts, research notes, and back catalogs
        into source-cited answers your subscribers can use without you building
        a chatbot or opening the archive to everyone.
      </p>
      <div className="flex w-full max-w-[min(342px,calc(100vw-3rem))] flex-wrap gap-3 mt-10 items-center sm:max-w-none">
        <a
          href="/login"
          className={cn(
            buttonVariants({ variant: 'signal' }),
            'h-11 w-full rounded px-5 text-[15px] font-medium normal-case tracking-normal sm:w-auto',
            'active:translate-y-px active:shadow-[var(--shadow-press)] active:scale-[0.97]'
          )}
        >
          Start with your archive
        </a>
        <a
          href="#answer"
          className={cn(
            'group inline-flex w-full items-center gap-1 h-11 px-5 rounded text-[15px] font-medium sm:w-auto',
            'bg-transparent text-[var(--fg)] border border-[var(--hairline-strong)]',
            'transition-[color,background-color,transform] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--bg-raised)] active:scale-[0.97]'
          )}
        >
          See a cited answer
          <span className="transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)] group-hover:translate-x-0.5">
            →
          </span>
        </a>
      </div>
      <div className="mt-16 md:mt-20 flex w-full max-w-[min(342px,calc(100vw-3rem))] flex-wrap items-center gap-3 font-mono text-[13px] text-[var(--fg-subtle)] sm:max-w-none">
        <span
          className="pulse-dot block w-2 h-2 rounded-full bg-[var(--live)]"
          style={{
            boxShadow:
              '0 0 0 3px color-mix(in oklab, var(--live) 22%, transparent)',
          }}
        />
        <span>subscriber link →</span>
        <span className="break-all text-[var(--fg)] tabular-nums">
          {sampleDisplayUrl}
        </span>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="box-border w-full px-6 md:px-10 py-20 md:py-24 max-w-[1200px] mx-auto"
      style={{ maxWidth: 'min(1200px, 100vw)' }}
    >
      <div className="flex items-baseline gap-6 mb-12">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--fg-subtle)]">
          How it works
        </div>
        <div className="flex-1 h-px bg-[var(--hairline)]" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-0">
        {STEPS.map((s, i) => (
          <div
            key={s.n}
            className={cn(
              'min-w-0 md:pr-8',
              i < 2 && 'md:border-r md:border-[var(--hairline)]',
              i > 0 && 'md:pl-8'
            )}
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--fg-subtle)]">
              {s.n}
            </div>
            <h3 className="font-display text-[26px] md:text-[28px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--fg)] mt-3 mb-3 text-balance">
              {s.title}
            </h3>
            <p className="m-0 max-w-[min(342px,calc(100vw-3rem))] text-[15px] leading-[1.6] text-[var(--fg-muted)] sm:max-w-none">
              {s.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function AuthorValue() {
  return (
    <section className="box-border w-full px-6 md:px-10 py-16 md:py-20 bg-[var(--bg-elevated)] border-y border-[var(--hairline)]">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-baseline gap-6 mb-10">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--fg-subtle)]">
            Author promise
          </div>
          <div className="flex-1 h-px bg-[var(--hairline)]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-10 md:gap-16 items-start">
          <div>
            <h2 className="font-display text-[36px] md:text-[48px] font-medium leading-[1.05] tracking-[-0.02em] text-[var(--fg)] m-0 text-balance">
              Give subscribers answers from your work, not a generic summary of
              the web.
            </h2>
            <p className="font-display italic text-[18px] md:text-[20px] leading-[1.45] text-[var(--fg-muted)] mt-5 max-w-[44ch]">
              Your archive becomes a useful subscriber benefit at the moment
              readers are trying to understand, decide, or remember.
            </p>
            <p className="text-[14.5px] leading-[1.6] text-[var(--fg-muted)] mt-5 max-w-[52ch]">
              You choose the durable material, Lore indexes it, and subscriber
              eligibility stays tied to your paid list. No public archive, no
              chatbot persona, and no technical setup to explain.
            </p>
          </div>
          <div className="rounded-lg border border-[var(--hairline)] bg-[color-mix(in_oklab,var(--reward)_8%,var(--bg-raised))] p-5 text-[14px] leading-[1.6] text-[var(--fg-muted)] shadow-[var(--shadow-2)]">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--reward)]">
              Subscriber note
            </div>
            <p className="mt-4 font-display text-[22px] leading-[1.15] text-[var(--fg)]">
              Paid members can now ask the full archive questions.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              {[
                'Find what we have said about a topic.',
                'Pull citations before a meeting or draft.',
                'Revisit older essays without searching an inbox.',
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <span className="mt-2 block w-1.5 h-1.5 rounded-full bg-[var(--accent-text)]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CitedAnswerProof({ sampleDisplayUrl }: { sampleDisplayUrl: string }) {
  return (
    <section
      id="answer"
      className="box-border w-full px-6 md:px-10 py-16 md:py-20 bg-[var(--bg-deep)] border-y border-[var(--hairline)]"
    >
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-wrap items-baseline gap-4 md:gap-6 mb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--fg-subtle)]">
            Subscriber answer
          </div>
          <div className="hidden sm:block flex-1 h-px bg-[var(--hairline)]" />
          <span className="break-all font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--fg-subtle)]">
            {sampleDisplayUrl}
          </span>
        </div>
        <div className="rounded-lg border border-[var(--hairline)] bg-[var(--bg-raised)] shadow-[var(--shadow-3)] overflow-hidden">
          <div className="border-b border-[var(--hairline)] px-5 py-4">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--fg-subtle)]">
              Question from a subscriber
            </div>
            <p className="mt-2 font-display text-[22px] leading-[1.3] text-[var(--fg)]">
              What have you written about keeping trust when the paid tier
              grows?
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-0">
            <div className="px-5 py-5 md:px-6 md:py-6">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--accent-text)]">
                Source-cited answer
              </div>
              <p className="mt-3 max-w-[62ch] text-[16px] leading-[1.7] text-[var(--fg-muted)]">
                You have argued that trust compounds when paid subscribers get
                more useful access to the work they already value. The move is
                not to publish more for its own sake; it is to make the archive
                easier to consult at the moment a reader needs context.
              </p>
              <p className="mt-4 max-w-[62ch] text-[16px] leading-[1.7] text-[var(--fg-muted)]">
                The strongest line across the archive is simple: paid work
                renews when subscribers can turn it back into decisions,
                references, and reminders.
              </p>
            </div>
            <div className="border-t border-[var(--hairline)] lg:border-t-0 lg:border-l px-5 py-5 md:px-6 md:py-6">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--fg-subtle)]">
                Sources
              </div>
              <div className="mt-4 flex flex-col gap-3">
                {SOURCE_CARDS.map((source) => (
                  <div
                    key={source.title}
                    className="rounded border border-[var(--hairline)] bg-[var(--bg-deep)] px-4 py-3"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--reward)]">
                      {source.label}
                    </div>
                    <div className="mt-1 text-[14px] font-medium text-[var(--fg)]">
                      {source.title}
                    </div>
                    <div className="mt-1 text-[12px] text-[var(--fg-subtle)]">
                      {source.meta}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MarketingFooter() {
  return (
    <footer className="box-border w-full px-6 md:px-10 py-14 md:py-16 border-t border-[var(--hairline)] bg-[var(--bg-deep)]">
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr] gap-10 md:gap-12 items-start">
        <div>
          <a href="/">
            <Logo wordmarkSize={20} wordmarkClassName="text-[var(--fg)]" />
          </a>
          <div className="font-display italic text-[17px] text-[var(--fg-subtle)] mt-3">
            paid archives, useful when subscribers need them.
          </div>
        </div>
        {(
          [
            {
              h: 'Product',
              l: [
                { label: 'Discover', href: '#discover' },
                { label: 'How it works', href: '#how-it-works' },
                { label: 'Cited answers', href: '#answer' },
              ],
            },
            {
              h: 'Creators',
              l: [
                { label: 'Docs', href: 'https://docs.findlore.dev' },
                { label: 'Import guide', href: 'https://docs.findlore.dev' },
                { label: 'Byline policy', href: 'https://docs.findlore.dev' },
                { label: 'Support', href: 'mailto:hello@findlore.dev' },
              ],
            },
            {
              h: 'Company',
              l: [
                { label: 'About', href: '#' },
                { label: 'Contact', href: 'mailto:hello@findlore.dev' },
                { label: 'Privacy', href: '/privacy' },
                { label: 'Terms', href: '/tos' },
              ],
            },
          ] as const
        ).map((c) => (
          <div key={c.h}>
            <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--fg-subtle)] mb-3">
              {c.h}
            </div>
            <div className="flex flex-col gap-2">
              {c.l.map((x) => (
                <a
                  key={x.label}
                  href={x.href}
                  className="text-[14px] text-[var(--fg-muted)] hover:text-[var(--fg)] transition-colors"
                >
                  {x.label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="max-w-[1200px] mx-auto mt-12 pt-6 border-t border-[var(--hairline)] flex flex-wrap items-center justify-between gap-3 font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--fg-subtle)]">
        <span>© 2026 Lore</span>
        <span>source-cited access for paid archives</span>
      </div>
    </footer>
  );
}
